import { useEffect } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useMutation, useQuery } from '@apollo/client/react';
import { Check, Plus, Trash2 } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { Text } from '@/components/ui/text';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
} from '@/components/ui/drawer';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Icon } from '@/components/ui/icon';
import { BaseModalProps } from '@/contexts/ModalContext';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import type { SportActivityCategoryRecord } from '~/powersync/generated/schema';
import { useLocalizedValue } from '@/hooks/useLocalizedValue';
import { db } from '@/lib/local-api';
import {
  GetSportActivityDocument,
  GetSportActivityQuery,
  GetSportActivityQueryVariables,
  Language,
  Muscle,
  MuscleGroup,
  namedOperations,
  SaveSportActivityDocument,
  SaveSportActivityMutation,
  SaveSportActivityMutationVariables,
  WorkoutSetDimension,
} from '~/graphql/generated/graphql';

export interface SportActivityFormDialogProps extends BaseModalProps {
  /**
   * ID of sport activity to edit. If not provided, creates new activity.
   */
  sportActivityId?: string;
  /**
   * Called when a sport activity is successfully saved
   */
  onSportActivitySaved?: (id: string) => void;
  /**
   * Portal host name for custom dialog placement
   */
  portalHost?: string;
}

interface SportActivityFormData {
  name: string;
  categoryId: string;
  defaultDimensions: WorkoutSetDimension[];
  muscleTargets: {
    muscleGroup: MuscleGroup;
    muscle: Muscle;
    intensity: number;
  }[];
}

const MUSCLE_OPTIONS: Record<MuscleGroup, Muscle[]> = {
  [MuscleGroup.Arms]: [Muscle.Biceps, Muscle.Triceps, Muscle.Forearms],
  [MuscleGroup.Legs]: [
    Muscle.Quadriceps,
    Muscle.Hamstrings,
    Muscle.Calves,
    Muscle.Glutes,
  ],
  [MuscleGroup.Chest]: [Muscle.PectoralisMajor, Muscle.PectoralisMinor],
  [MuscleGroup.Back]: [
    Muscle.LatissimusDorsi,
    Muscle.Trapezius,
    Muscle.Rhomboids,
    Muscle.LowerBack,
  ],
  [MuscleGroup.Shoulders]: [Muscle.Deltoids, Muscle.RotatorCuff],
  [MuscleGroup.Core]: [Muscle.RectusAbdominis, Muscle.Obliques],
  [MuscleGroup.Cardiovascular]: [Muscle.Heart],
};

const CategoryPill = ({
  category,
  isActive,
  onPress,
}: {
  category: SportActivityCategoryRecord;
  isActive: boolean;
  onPress: () => void;
}) => {
  const localizedTitle = useLocalizedValue(category.title);
  return (
    <Pressable
      onPress={onPress}
      className={`rounded-xl px-4 py-2.5 ${
        isActive ? 'bg-accent' : 'bg-surface-elevated'
      }`}
    >
      <Text
        className={`text-sm font-medium ${
          isActive ? 'text-accent-text font-semibold' : 'text-text-secondary'
        }`}
      >
        {localizedTitle}
      </Text>
    </Pressable>
  );
};

export const SportActivityFormDialog = ({
  sportActivityId,
  onClose,
  onSportActivitySaved,
  portalHost,
}: SportActivityFormDialogProps) => {
  const { t } = useTranslation('workout');
  const isEditMode = !!sportActivityId;

  const DIMENSION_OPTIONS = [
    { value: WorkoutSetDimension.Reps, label: t('dimension.REPS.name') },
    { value: WorkoutSetDimension.Weight, label: t('dimension.WEIGHT.name') },
    { value: WorkoutSetDimension.Time, label: t('dimension.TIME.name') },
    {
      value: WorkoutSetDimension.Distance,
      label: t('dimension.DISTANCE.name'),
    },
    {
      value: WorkoutSetDimension.Calories,
      label: t('dimension.CALORIES.name'),
    },
    {
      value: WorkoutSetDimension.HeartRate,
      label: t('dimension.HEART_RATE.name'),
    },
  ];

  // Query all categories
  const { data: categories } =
    useLivePowerSyncQuery<SportActivityCategoryRecord>(
      db.SportActivity.queries.findAllCategories(),
      [],
    );

  // Query existing sport activity for edit mode
  const { data: sportActivityData, loading: loadingActivity } = useQuery<
    GetSportActivityQuery,
    GetSportActivityQueryVariables
  >(GetSportActivityDocument, {
    variables: { id: sportActivityId! },
    skip: !isEditMode,
  });

  // Form setup
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
    watch,
    getValues,
  } = useForm<SportActivityFormData>({
    defaultValues: {
      name: '',
      categoryId: categories?.[0]?.id ?? '',
      defaultDimensions: [WorkoutSetDimension.Reps],
      muscleTargets: [
        {
          muscleGroup: MuscleGroup.Arms,
          muscle: Muscle.Biceps,
          intensity: 100,
        },
      ],
    },
  });

  useEffect(() => {
    const cat = getValues('categoryId');
    if (!cat) {
      setValue('categoryId', categories?.[0]?.id ?? '');
    }
  }, [categories, getValues, setValue]);

  const {
    fields: muscleTargetFields,
    append: appendMuscleTarget,
    remove: removeMuscleTarget,
  } = useFieldArray({
    control,
    name: 'muscleTargets',
  });

  // Load existing data in edit mode
  useEffect(() => {
    if (sportActivityData?.sportActivity && isEditMode) {
      const activity = sportActivityData.sportActivity;
      reset({
        name: activity.name[Language.En] || '',
        categoryId: activity.category.id,
        defaultDimensions: activity.defaultDimensions || [
          WorkoutSetDimension.Reps,
        ],
        muscleTargets:
          activity.muscleTargets.length > 0
            ? activity.muscleTargets.map((mt) => ({
                muscleGroup: mt.muscleGroup,
                muscle: mt.muscle,
                intensity: mt.intensity,
              }))
            : [
                {
                  muscleGroup: MuscleGroup.Arms,
                  muscle: Muscle.Biceps,
                  intensity: 100,
                },
              ],
      });
    }
  }, [sportActivityData, isEditMode, reset]);

  // Save mutation
  const [saveMutation, { loading: saving }] = useMutation<
    SaveSportActivityMutation,
    SaveSportActivityMutationVariables
  >(SaveSportActivityDocument, {
    refetchQueries: [namedOperations.Query.GetSportActivity],
    awaitRefetchQueries: false,
  });

  // Handlers
  const selectedDimensions = watch('defaultDimensions');

  const toggleDimension = (dimension: WorkoutSetDimension) => {
    if (selectedDimensions.includes(dimension)) {
      // keep at least one metric selected
      if (selectedDimensions.length > 1) {
        setValue(
          'defaultDimensions',
          selectedDimensions.filter((d) => d !== dimension),
          { shouldValidate: true },
        );
      }
    } else if (selectedDimensions.length < 3) {
      setValue('defaultDimensions', [...selectedDimensions, dimension], {
        shouldValidate: true,
      });
    }
  };

  const handleAddMuscleTarget = () => {
    appendMuscleTarget({
      muscleGroup: MuscleGroup.Arms,
      muscle: Muscle.Biceps,
      intensity: 100,
    });
  };

  const onSubmit = async (data: SportActivityFormData) => {
    try {
      const result = await saveMutation({
        variables: {
          id: isEditMode ? sportActivityId : undefined,
          input: {
            name: { [Language.En]: data.name.trim() },
            categoryId: data.categoryId,
            defaultDimensions: data.defaultDimensions,
            muscleTargets: data.muscleTargets.map((target) => ({
              muscleGroup: target.muscleGroup,
              muscle: target.muscle,
              intensity: target.intensity,
            })),
          },
        },
      });

      if (result.data?.saveSportActivity?.id) {
        onSportActivitySaved?.(result.data.saveSportActivity.id);
        onClose?.();
      }
    } catch (error) {
      console.error('Failed to save sport activity:', error);
    }
  };

  const loading = loadingActivity || saving;

  // Currently selected category (for active pill state)
  const categoryId = watch('categoryId');

  return (
    <Drawer open={true} onOpenChange={() => onClose()}>
      <DrawerContent>
        <DrawerHeader onClose={onClose}>
          <View className="gap-0.5">
            <Text className="text-lg font-sora-semibold text-text-primary">
              {isEditMode ? t('form.title.edit') : t('createActivity.title')}
            </Text>
            <Text className="text-sm text-text-secondary">
              {t('createActivity.subtitle')}
            </Text>
          </View>
        </DrawerHeader>

        <DrawerBody>
          <ScrollView
            className="flex-1"
            contentContainerClassName="px-5 pb-5"
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            <View className="gap-6">
              {/* Name */}
              <View className="gap-2">
                <Text className="text-sm font-medium text-text-secondary">
                  {t('createActivity.fields.name.label')}
                </Text>
                <Controller
                  control={control}
                  name="name"
                  rules={{
                    required: true,
                    minLength: 1,
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Input
                      testID="exercise-form-name"
                      placeholder={t('createActivity.fields.name.placeholder')}
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      className={`h-12 rounded-xl bg-surface-elevated px-4 ${
                        errors.name ? 'border border-negative' : 'border-0'
                      }`}
                    />
                  )}
                />
              </View>

              {/* Category */}
              <View className="gap-2">
                <Text className="text-sm font-medium text-text-secondary">
                  {t('createActivity.fields.category.label')}
                </Text>
                <View className="flex-row flex-wrap gap-2">
                  {categories?.map((category) => (
                    <CategoryPill
                      key={category.id}
                      category={category}
                      isActive={category.id === categoryId}
                      onPress={() =>
                        setValue('categoryId', category.id, {
                          shouldValidate: true,
                        })
                      }
                    />
                  ))}
                </View>
              </View>

              {/* Tracked metrics (multi-select, 1-3) */}
              <View className="gap-2">
                <View className="flex-row items-end justify-between">
                  <View>
                    <Text className="text-sm font-medium text-text-secondary">
                      {t('createActivity.fields.dimensions.label')}
                    </Text>
                    <Text className="text-xs text-text-muted">
                      {t('createActivity.fields.dimensions.hint')}
                    </Text>
                  </View>
                  <Text className="text-sm font-semibold text-text-secondary">
                    {selectedDimensions.length} / 3
                  </Text>
                </View>
                <View className="flex-row flex-wrap gap-2">
                  {DIMENSION_OPTIONS.map((opt) => {
                    const isActive = selectedDimensions.includes(opt.value);
                    const atMax = selectedDimensions.length >= 3;
                    return (
                      <Pressable
                        key={opt.value}
                        onPress={() => toggleDimension(opt.value)}
                        disabled={!isActive && atMax}
                        className={`flex-row items-center gap-1.5 rounded-xl border px-4 py-2.5 ${
                          isActive
                            ? 'bg-accent-soft border-accent'
                            : 'bg-surface-elevated border-transparent'
                        } ${!isActive && atMax ? 'opacity-40' : ''}`}
                      >
                        {isActive && (
                          <Icon as={Check} size={14} className="text-accent" />
                        )}
                        <Text
                          className={`text-sm font-medium ${
                            isActive ? 'text-accent' : 'text-text-secondary'
                          }`}
                        >
                          {opt.label}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              </View>

              {/* Target muscles */}
              <View className="gap-3">
                <View className="flex-row items-end justify-between">
                  <View>
                    <Text className="text-sm font-medium text-text-secondary">
                      {t('createActivity.fields.muscleTargets.label')}
                    </Text>
                    <Text className="text-xs text-text-muted">
                      {t('createActivity.fields.muscleTargets.hint')}
                    </Text>
                  </View>
                  <Text className="text-sm font-semibold text-text-secondary">
                    {t('createActivity.fields.muscleTargets.count', {
                      count: muscleTargetFields.length,
                    })}
                  </Text>
                </View>
                {muscleTargetFields.map((field, index) => (
                  <View
                    key={field.id}
                    className="gap-3 rounded-xl border border-border bg-surface-elevated p-4"
                  >
                    <View className="flex-row items-end gap-2">
                      <View className="flex-1 gap-1">
                        <Text className="text-xs text-text-muted">
                          {t('createActivity.fields.muscleGroup')}
                        </Text>
                        <Controller
                          control={control}
                          name={`muscleTargets.${index}.muscleGroup`}
                          render={({ field: { onChange, value } }) => {
                            const label = t(`muscleGroup.${value}`);
                            return (
                              <Select
                                value={{ value, label }}
                                onValueChange={(option) => {
                                  if (option) {
                                    const newGroup =
                                      option.value as MuscleGroup;
                                    onChange(newGroup);
                                    // Reset muscle to first available
                                    const availableMuscles =
                                      MUSCLE_OPTIONS[newGroup];
                                    setValue(
                                      `muscleTargets.${index}.muscle`,
                                      availableMuscles[0],
                                    );
                                  }
                                }}
                              >
                                <SelectTrigger className="h-12 w-full rounded-xl sm:h-12">
                                  <SelectValue placeholder={label} />
                                </SelectTrigger>
                                <SelectContent className="z-[10000]">
                                  {Object.values(MuscleGroup).map((group) => (
                                    <SelectItem
                                      key={group}
                                      value={group}
                                      label={t(`muscleGroup.${group}`)}
                                    >
                                      {t(`muscleGroup.${group}`)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            );
                          }}
                        />
                      </View>
                      <View className="flex-1 gap-1">
                        <Text className="text-xs text-text-muted">
                          {t('createActivity.fields.muscle')}
                        </Text>
                        <Controller
                          control={control}
                          name={`muscleTargets.${index}.muscle`}
                          render={({ field: { onChange, value } }) => {
                            const muscleGroup = watch(
                              `muscleTargets.${index}.muscleGroup`,
                            );
                            const label = t(`muscle.${value}`);
                            return (
                              <Select
                                value={{ value, label }}
                                onValueChange={(option) =>
                                  option && onChange(option.value as Muscle)
                                }
                              >
                                <SelectTrigger className="h-12 w-full rounded-xl sm:h-12">
                                  <SelectValue placeholder={label} />
                                </SelectTrigger>
                                <SelectContent className="z-[10000]">
                                  {MUSCLE_OPTIONS[
                                    muscleGroup as MuscleGroup
                                  ]?.map((muscle) => (
                                    <SelectItem
                                      key={muscle}
                                      value={muscle}
                                      label={t(`muscle.${muscle}`)}
                                    >
                                      {t(`muscle.${muscle}`)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            );
                          }}
                        />
                      </View>
                      <Button
                        variant="surface"
                        size="icon"
                        onPress={() => removeMuscleTarget(index)}
                        className="h-12 w-12 rounded-xl border border-input bg-background"
                      >
                        <Icon
                          as={Trash2}
                          size={18}
                          className="text-text-secondary"
                        />
                      </Button>
                    </View>
                    {/* Intensity slider */}
                    <Controller
                      control={control}
                      name={`muscleTargets.${index}.intensity`}
                      render={({ field: { onChange, value } }) => (
                        <View className="gap-1">
                          <View className="flex-row items-center justify-between">
                            <Text className="text-xs text-text-muted">
                              {t('createActivity.fields.intensity')}
                            </Text>
                            <Text className="text-sm font-semibold text-accent-icon">
                              {value} %
                            </Text>
                          </View>
                          <Slider
                            value={value}
                            onValueChange={onChange}
                            minimumValue={0}
                            maximumValue={100}
                            step={1}
                          />
                        </View>
                      )}
                    />
                  </View>
                ))}
                <Button
                  variant="ghost"
                  onPress={handleAddMuscleTarget}
                  className="flex-row gap-2"
                >
                  <Icon as={Plus} size={18} className="text-text-secondary" />
                  <Text className="text-sm font-medium text-text-secondary">
                    {t('createActivity.actions.addMuscle')}
                  </Text>
                </Button>
              </View>
            </View>
          </ScrollView>

          {/* Actions */}
          <View className="flex-row gap-2 px-5 pb-5 pt-4 border-t border-border">
            <Button
              variant="secondary"
              className="flex-1"
              onPress={onClose}
              disabled={loading}
            >
              <Text>{t('createActivity.actions.cancel')}</Text>
            </Button>
            <Button
              testID="exercise-form-save"
              className="flex-1"
              onPress={handleSubmit(onSubmit)}
              disabled={!isValid || loading}
              loading={loading}
            >
              <Text>
                {isEditMode
                  ? t('form.buttons.update')
                  : t('createActivity.actions.create')}
              </Text>
            </Button>
          </View>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
